import { TURN_DURATION_S } from './constants.ts';
import { clamp, segmentIntersection } from './math.ts';
import { gateWidth } from './map/checkpoints.ts';
import { closestPointOnSegment, distance, mapLapCount } from './map/document.ts';
import type { Checkpoint, MapDocument } from './map/types.ts';
import type { MovePath } from './path.ts';
import { clipPolylineToProgress, sampleMovePath } from './path.ts';
import type { Vec2 } from './types.ts';

/** Below this a gate's two flags are treated as the same point (two clicks on one spot). */
const DEGENERATE_GATE_WIDTH = 1e-6;

/** World-unit tolerance for crossing a degenerate (point) gate. Matches the editor's flag radius. */
const POINT_GATE_TOLERANCE = 16;

/**
 * Closest approach that still counts as a near miss, in *screen* pixels.
 * Convert to world with `nearMissWorld(px, zoom)` so the band stays readable when zoomed out.
 */
export const NEAR_MISS_PX = 28;
export const NEAR_MISS_PX_MIN = 0;
export const NEAR_MISS_PX_STEP = 4;
export const NEAR_MISS_PX_MAX = 120;

/** @deprecated Prefer `NEAR_MISS_PX` + zoom; kept as the zoom-1 world default. */
export const NEAR_MISS_WORLD = NEAR_MISS_PX;

/** World-unit near-miss band for the current camera zoom. */
export function nearMissWorld(screenPx: number, zoom: number): number {
  if (!Number.isFinite(screenPx) || screenPx <= 0) {
    return 0;
  }

  const z = Number.isFinite(zoom) && zoom > 1e-6 ? zoom : 1;
  return screenPx / z;
}

export type RaceState = {
  /** 1-based lap the boat is on. Stays at `map.laps` once the race is complete. */
  lap: number;
  /** Gates cleared on the current lap. Cleared again when an intermediate lap line counts. */
  passedCheckpointIds: ReadonlySet<string>;
  complete: boolean;
};

/** An eligible gate the path skimmed without crossing. */
export type NearMiss = {
  id: string;
  /** Closest point on the move path to the gate. */
  point: Vec2;
};

export function createRaceState(): RaceState {
  return { lap: 1, passedCheckpointIds: new Set(), complete: false };
}

/** Player-facing gate name for HUD tooltips: Goal, or 1-based Gate N in map order. */
export function gateHudLabel(map: MapDocument, checkpointId: string): string {
  const index = map.checkpoints.findIndex((checkpoint) => checkpoint.id === checkpointId);
  const checkpoint = index >= 0 ? map.checkpoints[index] : undefined;

  if (checkpoint?.isGoal) {
    return 'Goal';
  }

  if (index >= 0) {
    return `Gate ${index + 1}`;
  }

  return 'Gate';
}

/** True when every numbered non-goal gate with a strictly smaller order is already in `passed`. */
function lowerOrdersCleared(
  map: MapDocument,
  order: number,
  passed: ReadonlySet<string>,
): boolean {
  return map.checkpoints.every(
    (checkpoint) =>
      checkpoint.isGoal ||
      checkpoint.order === null ||
      checkpoint.order >= order ||
      passed.has(checkpoint.id),
  );
}

/**
 * Order-null gates are each their own job. A numbered gate is one of several alternatives:
 * sailing through one of them clears every other non-goal gate that shares that number.
 * The goal is never retired this way.
 */
function markCleared(map: MapDocument, passed: Set<string>, checkpoint: Checkpoint): void {
  if (checkpoint.isGoal || checkpoint.order === null) {
    passed.add(checkpoint.id);
    return;
  }

  for (const other of map.checkpoints) {
    if (!other.isGoal && other.order === checkpoint.order) {
      passed.add(other.id);
    }
  }
}

function othersCleared(map: MapDocument, passed: ReadonlySet<string>): boolean {
  return map.checkpoints.every((checkpoint) => checkpoint.isGoal || passed.has(checkpoint.id));
}

function eligible(map: MapDocument, checkpoint: Checkpoint, passed: ReadonlySet<string>): boolean {
  if (checkpoint.isGoal) {
    return othersCleared(map, passed);
  }

  if (checkpoint.order === null) {
    return true;
  }

  return lowerOrdersCleared(map, checkpoint.order, passed);
}

/**
 * Still-open gates a crossing would count for right now, under the same order, goal, and
 * lap rules `advanceRace` scores with. Bots aim at these; the HUD does not use it.
 */
export function eligibleCheckpoints(map: MapDocument, race: RaceState): Checkpoint[] {
  if (race.complete) {
    return [];
  }

  return map.checkpoints.filter(
    (checkpoint) =>
      !race.passedCheckpointIds.has(checkpoint.id) &&
      eligible(map, checkpoint, race.passedCheckpointIds),
  );
}

/**
 * First place a path crosses the infinite line through `a`→`b`.
 * `tGate` is the parameter on that line (`0` at `a`, `1` at `b`; outside that is past a pole).
 */
function firstInfiniteLineCrossing(
  points: readonly Vec2[],
  a: Vec2,
  b: Vec2,
): { point: Vec2; tGate: number; tPath: number } | null {
  const sx = b.x - a.x;
  const sy = b.y - a.y;

  if (sx * sx + sy * sy < DEGENERATE_GATE_WIDTH * DEGENERATE_GATE_WIDTH) {
    return null;
  }

  for (let i = 0; i < points.length - 1; i += 1) {
    const from = points[i];
    const to = points[i + 1];

    if (!from || !to) {
      continue;
    }

    const rx = to.x - from.x;
    const ry = to.y - from.y;
    const denom = rx * sy - ry * sx;

    if (Math.abs(denom) < 1e-12) {
      continue;
    }

    const qpx = a.x - from.x;
    const qpy = a.y - from.y;
    const tPath = (qpx * sy - qpy * sx) / denom;
    const tGate = (qpx * ry - qpy * rx) / denom;

    if (tPath < 0 || tPath > 1) {
      continue;
    }

    return {
      point: { x: from.x + tPath * rx, y: from.y + tPath * ry },
      tGate,
      tPath,
    };
  }

  return null;
}

type PathEvent = {
  kind: 'cross' | 'miss';
  checkpoint: Checkpoint;
  distance: number;
  point: Vec2;
};

function alongSegment(from: Vec2, to: Vec2, point: Vec2): number {
  const span = distance(from, to);

  if (span <= DEGENERATE_GATE_WIDTH) {
    return 0;
  }

  return Math.min(span, distance(from, point));
}

function collectEvents(
  map: MapDocument,
  points: readonly Vec2[],
  maxMissDist: number,
): PathEvent[] {
  const events: PathEvent[] = [];
  let travelled = 0;

  for (let i = 0; i < points.length - 1; i += 1) {
    const from = points[i];
    const to = points[i + 1];

    if (!from || !to) {
      continue;
    }

    const span = distance(from, to);

    for (const checkpoint of map.checkpoints) {
      if (gateWidth(checkpoint) > DEGENERATE_GATE_WIDTH) {
        const hit = segmentIntersection(from, to, checkpoint.a, checkpoint.b);

        if (hit) {
          events.push({
            kind: 'cross',
            checkpoint,
            distance: travelled + alongSegment(from, to, hit),
            point: hit,
          });
        }

        if (maxMissDist > 0) {
          const crossing = firstInfiniteLineCrossing([from, to], checkpoint.a, checkpoint.b);

          if (crossing && (crossing.tGate < 0 || crossing.tGate > 1)) {
            const pole = crossing.tGate < 0 ? checkpoint.a : checkpoint.b;

            if (distance(crossing.point, pole) <= maxMissDist) {
              events.push({
                kind: 'miss',
                checkpoint,
                distance: travelled + alongSegment(from, to, crossing.point),
                point: crossing.point,
              });
            }
          }
        }
      } else {
        const closest = closestPointOnSegment(checkpoint.a, from, to);

        if (distance(checkpoint.a, closest) <= POINT_GATE_TOLERANCE) {
          events.push({
            kind: 'cross',
            checkpoint,
            distance: travelled + alongSegment(from, to, closest),
            point: closest,
          });
        }
      }
    }

    travelled += span;
  }

  events.sort((left, right) => {
    if (left.distance !== right.distance) {
      return left.distance - right.distance;
    }

    if (left.kind !== right.kind) {
      return left.kind === 'cross' ? -1 : 1;
    }

    if (left.checkpoint.isGoal !== right.checkpoint.isGoal) {
      return left.checkpoint.isGoal ? 1 : -1;
    }

    const leftOrder = left.checkpoint.order ?? Number.POSITIVE_INFINITY;
    const rightOrder = right.checkpoint.order ?? Number.POSITIVE_INFINITY;

    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }

    if (left.checkpoint.id < right.checkpoint.id) {
      return -1;
    }

    if (left.checkpoint.id > right.checkpoint.id) {
      return 1;
    }

    return 0;
  });

  // A crossing that lands on a sample is reported by both segments that share that vertex.
  const unique: PathEvent[] = [];

  for (const event of events) {
    const previous = unique.at(-1);

    if (
      previous &&
      previous.kind === event.kind &&
      previous.checkpoint.id === event.checkpoint.id &&
      Math.abs(previous.distance - event.distance) <= 1e-4
    ) {
      continue;
    }

    unique.push(event);
  }

  return unique;
}

type PendingMiss = {
  id: string;
  lap: number;
  point: Vec2;
};

type ScoredPath = {
  race: RaceState;
  /** Gates whose crossing counted. Alternatives retired by a shared number are not listed. */
  crossedIds: readonly string[];
  misses: readonly NearMiss[];
};

/**
 * Walks crossings in path order. A numbered gate counts only after lower numbers are already
 * cleared. The checkered line counts only after every other gate, and only at a crossing that
 * comes after those gates on this path. An earlier cut across the line does nothing. When the
 * line counts and laps remain, the passed set clears and the suffix of the path scores the next
 * lap. The last line finishes the race and leaves `lap` at `map.laps`.
 */
function scorePath(
  map: MapDocument,
  race: RaceState,
  points: readonly Vec2[],
  maxMissDist: number,
): ScoredPath {
  if (race.complete) {
    return { race, crossedIds: [], misses: [] };
  }

  const lapCount = mapLapCount(map.laps);
  let lap = race.lap;
  let passed = new Set(race.passedCheckpointIds);
  let complete = false;
  const crossedIds: string[] = [];
  const misses: NearMiss[] = [];
  const pending: PendingMiss[] = [];

  const dropPending = (id: string, atLap: number): void => {
    for (let i = pending.length - 1; i >= 0; i -= 1) {
      const miss = pending[i];

      if (miss && miss.id === id && miss.lap === atLap) {
        pending.splice(i, 1);
      }
    }
  };

  const flushPending = (atLap: number | null): void => {
    for (let i = pending.length - 1; i >= 0; i -= 1) {
      const miss = pending[i];

      if (!miss || (atLap !== null && miss.lap !== atLap)) {
        continue;
      }

      misses.push({ id: miss.id, point: miss.point });
      pending.splice(i, 1);
    }
  };

  for (const event of collectEvents(map, points, maxMissDist)) {
    if (complete) {
      break;
    }

    const checkpoint = event.checkpoint;

    if (event.kind === 'miss') {
      const already = pending.some((miss) => miss.id === checkpoint.id && miss.lap === lap);

      if (!already && !passed.has(checkpoint.id) && eligible(map, checkpoint, passed)) {
        pending.push({ id: checkpoint.id, lap, point: event.point });
      }

      continue;
    }

    if (passed.has(checkpoint.id)) {
      continue;
    }

    if (checkpoint.isGoal) {
      if (!othersCleared(map, passed)) {
        continue;
      }

      dropPending(checkpoint.id, lap);

      if (lap >= lapCount) {
        passed.add(checkpoint.id);
        crossedIds.push(checkpoint.id);
        complete = true;
        break;
      }

      flushPending(lap);
      lap += 1;
      passed = new Set();
      continue;
    }

    if (checkpoint.order !== null && !lowerOrdersCleared(map, checkpoint.order, passed)) {
      continue;
    }

    markCleared(map, passed, checkpoint);
    dropPending(checkpoint.id, lap);

    if (checkpoint.order !== null) {
      for (const other of map.checkpoints) {
        if (!other.isGoal && other.order === checkpoint.order) {
          dropPending(other.id, lap);
        }
      }
    }

    crossedIds.push(checkpoint.id);
  }

  flushPending(null);

  return {
    race: { lap, passedCheckpointIds: passed, complete },
    crossedIds,
    misses,
  };
}

/**
 * Eligible, not-yet-passed gates the preview path would actually cross.
 * Uses the same order and lap rules as `advanceRace`, but only returns the gates the
 * path intersects — not the alternatives `markCleared` would also retire.
 */
export function previewedCheckpointIds(
  map: MapDocument,
  race: RaceState,
  path: MovePath | null,
): ReadonlySet<string> {
  if (race.complete || !path) {
    return new Set();
  }

  return new Set(scorePath(map, race, sampleMovePath(path), 0).crossedIds);
}

/**
 * Checks a just-travelled move path against the course and returns the updated race state.
 * Checkpoints with `order: null` may be crossed at any time and each must be sailed through.
 * A numbered gate only counts once every lower number has already been cleared; sailing through
 * one numbered gate also clears every other non-goal gate that shares that number. The checkered
 * line is not marked until every other checkpoint is already passed, and only when the crossing
 * sits after those gates along the path. With more than one lap, that crossing reopens every
 * gate and the rest of the path can score the next lap. `complete` requires the line on the last
 * lap.
 *
 * `progress` is the 0–1 fraction of the path the boat has already travelled. The full path is
 * scored at 1; a prefix is used while the hull is still moving so a gate is not marked until the
 * boat reaches it.
 */
export function advanceRace(
  map: MapDocument,
  race: RaceState,
  path: MovePath | null,
  progress = 1,
): RaceState {
  if (race.complete || !path || progress <= 0) {
    return race;
  }

  const points = clipPolylineToProgress(sampleMovePath(path), progress);
  return scorePath(map, race, points, 0).race;
}

/**
 * Eligible, still-open gates the path sailed *past* (crossed the infinite gate plane) outside
 * the poles, within `maxDist` of an endpoint — not a short approach that can still take the
 * gate next turn. Point gates are skipped (proximity already decides pass/fail). A skim after
 * the lap line belongs to the new lap.
 */
export function nearMissCheckpoints(
  map: MapDocument,
  race: RaceState,
  path: MovePath | null,
  maxDist = NEAR_MISS_WORLD,
): readonly NearMiss[] {
  if (race.complete || !path || maxDist <= 0) {
    return [];
  }

  return scorePath(map, race, sampleMovePath(path), maxDist).misses;
}

/**
 * Race state before each turn: index `i` is the state entering `paths[i]`, and the last entry is
 * the state after the final turn. Replay scores these once up front so a frame only has to
 * advance the single turn the hull is on.
 */
export function racePrefixStates(
  map: MapDocument,
  paths: readonly MovePath[],
): RaceState[] {
  let race = createRaceState();
  const states: RaceState[] = [race];

  for (const path of paths) {
    race = advanceRace(map, race, path, 1);
    states.push(race);
  }

  return states;
}

export function sameRaceProgress(a: RaceState, b: RaceState): boolean {
  if (
    a.lap !== b.lap ||
    a.complete !== b.complete ||
    a.passedCheckpointIds.size !== b.passedCheckpointIds.size
  ) {
    return false;
  }

  for (const id of a.passedCheckpointIds) {
    if (!b.passedCheckpointIds.has(id)) {
      return false;
    }
  }

  return true;
}

/**
 * A race milestone that can be stamped on the boat-clock. Numbered orders share one key
 * (any alternative clears the order). Order-null gates are each their own id. An intermediate
 * lap line is `{ kind: 'lap' }`. The finish is `{ kind: 'goal' }`.
 */
export type SplitKey =
  | { kind: 'order'; order: number; lap: number }
  | { kind: 'gate'; id: string; lap: number }
  | { kind: 'lap'; lap: number }
  | { kind: 'goal' };

export function splitKeyId(key: SplitKey): string {
  if (key.kind === 'order') {
    return `order:${key.lap}:${key.order}`;
  }

  if (key.kind === 'gate') {
    return `gate:${key.lap}:${key.id}`;
  }

  if (key.kind === 'lap') {
    return `lap:${key.lap}`;
  }

  return 'goal';
}

/** Higher is later on the course. Goal last; a lap line sits after that lap's gates. */
export function splitKeyRank(key: SplitKey): number {
  if (key.kind === 'goal') {
    return 1_000_000_000;
  }

  if (key.kind === 'lap') {
    return key.lap * 1_000_000 + 500_000;
  }

  if (key.kind === 'order') {
    return key.lap * 1_000_000 + 1_000 + key.order;
  }

  return key.lap * 1_000_000;
}

export type RaceSplit = {
  key: SplitKey;
  rounds: number;
  progress: number;
  timeMs: number;
};

function raceHasSplit(map: MapDocument, race: RaceState, key: SplitKey): boolean {
  if (key.kind === 'goal') {
    return race.complete;
  }

  if (key.kind === 'lap') {
    return race.lap > key.lap;
  }

  if (race.lap > key.lap) {
    return true;
  }

  if (race.lap < key.lap) {
    return false;
  }

  if (key.kind === 'gate') {
    return race.passedCheckpointIds.has(key.id);
  }

  return map.checkpoints.some(
    (checkpoint) =>
      !checkpoint.isGoal &&
      checkpoint.order === key.order &&
      race.passedCheckpointIds.has(checkpoint.id),
  );
}

/**
 * Smallest path progress in `(0, 1]` where `advanceRace` first records `key`.
 * `null` when this move does not newly pass that milestone.
 */
export function progressWhenPassed(
  map: MapDocument,
  raceBefore: RaceState,
  path: MovePath | null,
  key: SplitKey,
): number | null {
  if (!path || raceHasSplit(map, raceBefore, key)) {
    return null;
  }

  if (!raceHasSplit(map, advanceRace(map, raceBefore, path, 1), key)) {
    return null;
  }

  let lo = 0;
  let hi = 1;

  for (let i = 0; i < 32; i += 1) {
    const mid = (lo + hi) / 2;

    if (raceHasSplit(map, advanceRace(map, raceBefore, path, mid), key)) {
      hi = mid;
    } else {
      lo = mid;
    }
  }

  return hi;
}

/**
 * Smallest path progress in `(0, 1]` where `advanceRace` first marks the race complete.
 * `null` when this move does not finish the race. Progress is the same prefix `advanceRace`
 * already uses, not the speedometer reading at the line.
 */
export function finishProgress(
  map: MapDocument,
  raceBefore: RaceState,
  path: MovePath | null,
): number | null {
  return progressWhenPassed(map, raceBefore, path, { kind: 'goal' });
}

function courseSplitKeys(map: MapDocument, lap: number): SplitKey[] {
  const keys: SplitKey[] = [];
  const seen = new Set<string>();

  for (const checkpoint of map.checkpoints) {
    if (checkpoint.isGoal) {
      continue;
    }

    const key: SplitKey =
      checkpoint.order === null
        ? { kind: 'gate', id: checkpoint.id, lap }
        : { kind: 'order', order: checkpoint.order, lap };
    const id = splitKeyId(key);

    if (seen.has(id)) {
      continue;
    }

    seen.add(id);
    keys.push(key);
  }

  return keys;
}

/** New boat-clock stamps earned on this path. `rounds` is the 1-based move index. */
export function splitsFromMove(
  map: MapDocument,
  raceBefore: RaceState,
  path: MovePath | null,
  rounds: number,
): RaceSplit[] {
  const after = advanceRace(map, raceBefore, path, 1);
  const splits: RaceSplit[] = [];
  const lapCount = mapLapCount(map.laps);

  const push = (key: SplitKey): void => {
    if (raceHasSplit(map, raceBefore, key) || !raceHasSplit(map, after, key)) {
      return;
    }

    const progress = progressWhenPassed(map, raceBefore, path, key);

    if (progress === null) {
      return;
    }

    splits.push({
      key,
      rounds,
      progress,
      timeMs: calculatedTimeMs(rounds, progress),
    });
  };

  for (let lap = raceBefore.lap; lap <= after.lap; lap += 1) {
    for (const key of courseSplitKeys(map, lap)) {
      push(key);
    }

    if (lap < lapCount) {
      push({ kind: 'lap', lap });
    }
  }

  if (after.complete && !raceBefore.complete) {
    push({ kind: 'goal' });
  }

  return splits;
}

/** Boat time in ms: every full turn is `TURN_DURATION_S` seconds, plus how far along the last path the finish sat. */
export function calculatedTimeMs(rounds: number, progress: number): number {
  if (rounds <= 0) {
    return 0;
  }

  return Math.round((rounds - 1 + clamp(progress, 0, 1)) * TURN_DURATION_S * 1000);
}

/**
 * Boat-clock ms from a 0…`pathCount` cursor (one unit per turn). A mid-path finish
 * clamps the last turn to `lastFinishProgress` so the HUD stops when the race does.
 */
export function boatClockFromCursor(
  cursor: number,
  pathCount: number,
  lastFinishProgress = 1,
): number {
  if (
    !Number.isFinite(cursor) ||
    !Number.isFinite(pathCount) ||
    pathCount <= 0 ||
    cursor <= 0
  ) {
    return 0;
  }

  const finish = clamp(lastFinishProgress, 0, 1);
  const end = pathCount - 1 + finish;
  const capped = Math.min(Math.max(cursor, 0), end);

  if (capped >= end - 1e-9) {
    return calculatedTimeMs(pathCount, finish);
  }

  const whole = Math.floor(capped);
  const fraction = capped - whole;

  if (fraction < 1e-9) {
    return calculatedTimeMs(whole, 1);
  }

  return calculatedTimeMs(whole + 1, fraction);
}
